import React from 'react';
import { cn } from '../../../lib/utils';

interface ProductListSkeletonProps {
  className?: string;
  gridCols?: 1 | 2 | 3 | 4 | 5 | 6;
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  showPagination?: boolean;
  itemsCount?: number;
}

export function ProductListSkeleton({
  className = '',
  gap = 'md',
  showPagination = false,
  itemsCount = 12,
}: ProductListSkeletonProps) {
  // Generate grid classes - always 2 on mobile, 4 on desktop (matching ProductList)
  const getGridClasses = () => {
    return 'grid-cols-2 lg:grid-cols-4';
  };

  // Generate gap classes
  const getGapClasses = () => {
    switch (gap) {
      case 'sm':
        return 'gap-2 sm:gap-3';
      case 'md':
        return 'gap-4 sm:gap-6';
      case 'lg':
        return 'gap-6 sm:gap-8';
      case 'xl':
        return 'gap-8 sm:gap-10';
      default:
        return 'gap-4 sm:gap-6';
    }
  };

  // Calculate the number of skeleton items to show
  // Always show a full grid row to prevent layout shifts
  const getSkeletonCount = () => {
    const mobileCols = 2;
    const desktopCols = 4;

    // Always show at least 8 items (2 rows on desktop, 4 rows on mobile)
    // This ensures consistent layout and prevents glitches
    const minItems = Math.max(mobileCols * 2, desktopCols * 2);

    // If itemsCount is less than minItems, show minItems
    // If itemsCount is more, show the next complete row
    if (itemsCount <= minItems) {
      return minItems;
    }

    // Calculate how many complete rows we can show
    const completeRows = Math.ceil(itemsCount / desktopCols);
    return Math.max(completeRows * desktopCols, minItems);
  };

  const skeletonCount = getSkeletonCount();

  // Product item skeleton with smooth animation
  const ProductItemSkeleton = () => (
    <div className="flex flex-col bg-white overflow-hidden w-full max-sm:max-w-[156px] rounded-lg shadow-sm border border-secondary-100 skeleton-grid-item">
      {/* Product Image skeleton */}
      <div className="relative bg-secondary-50 overflow-hidden aspect-square w-full">
        <div className="w-full h-full skeleton-item"></div>
      </div>

      {/* Product Content skeleton */}
      <div className="flex flex-col gap-3 items-start p-3">
        {/* Product Name skeleton */}
        <div className="w-3/4 h-4 skeleton-item rounded"></div>

        {/* View Detail Button skeleton */}
        <div className="w-20 h-7 skeleton-item rounded"></div>
      </div>
    </div>
  );

  // Pagination skeleton with smooth animation
  const PaginationSkeleton = () => (
    <div className="px-4 sm:px-6 xl:px-4xl">
      <div className="mt-8 flex items-center justify-center gap-2">
        {/* Previous button */}
        <div className="w-8 h-8 skeleton-item rounded skeleton-stagger-1"></div>

        {/* Page numbers */}
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`w-8 h-8 skeleton-item rounded skeleton-stagger-${i + 1}`}></div>
          ))}
        </div>

        {/* Next button */}
        <div className="w-8 h-8 skeleton-item rounded skeleton-stagger-5"></div>

        {/* Items per page selector */}
        <div className="ml-4 w-24 h-8 skeleton-item rounded skeleton-stagger-6"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 skeleton-container">
      <div className={cn('grid px-6 sm:px-6 xl:px-4xl', getGapClasses(), getGridClasses(), className)}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <ProductItemSkeleton key={index} />
        ))}
      </div>

      {showPagination && <PaginationSkeleton />}
    </div>
  );
}
